import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Field, FieldProps, Form, Formik } from 'formik'

import { InsuranceCompany, InsuranceFormProps, LayoutItem } from '../../../ana-toolbox'
import { InsuranceType } from '../../../ana-toolbox'
import { FormLayout } from '../../components/form-layout/form-layout'
import { InsuranceField } from '../../components/insurance-field/insurance-field'
import { SubmitButton } from '../../components/submit-button/submit-button'
import { Translate } from '../../components/translate/translate'
import { insuranceFields as fields, insuranceFieldsValidationSchema } from '../../utils/fields/insurance'
import { getFormInitialErrors } from '../../utils/helpers/getFormInitialErrors'
import { SectionTitle } from '../../utils/shared/SectionTitle'

type CreateLayoutParams = {
  insuranceCompanies: InsuranceCompany[]
}

export const InsuranceForm = ({
  insuranceCompanies,
  onSubmit,
  initialValues,
  portalAttr,
  innerRef,
  loading,
  clinicEmail,
}: InsuranceFormProps) => {
  const insuranceFields = fields(clinicEmail)

  const insuranceLayout = ({ insuranceCompanies }: CreateLayoutParams): LayoutItem[] => [
    { type: 'field', field: insuranceFields.hasCareLevel, xs: 12 },
    {
      type: 'field',
      field: insuranceFields.careLevelLimitedInTime,
      xs: 12,
    },
    { type: 'field', field: insuranceFields.entitledToIntegrationAssistance, xs: 12 },
    {
      type: 'field',
      field: insuranceFields.integrationAssistanceBasedOnType,
      xs: 12,
    },
    { type: 'field', field: insuranceFields.type, xs: 12 },
    {
      type: 'custom',
      key: 'insuranceCompany',
      xs: 12,
      render: ({ values }) => {
        const { helperText, ...field } = insuranceFields.insuranceCompany

        return values.type === InsuranceType.PRIVATE ? (
          <InsuranceField companiesOptions={insuranceCompanies} {...field} helperText={helperText} />
        ) : null
      },
    },
    { type: 'field', field: insuranceFields.hasFamilyInsurance, xs: 12 },
    {
      type: 'custom',
      key: 'section-title',
      xs: 12,
      render: ({ values: { hasFamilyInsurance } }) => {
        return hasFamilyInsurance ? (
          <SectionTitle>
            <Translate>family_insurance_holder</Translate>
          </SectionTitle>
        ) : null
      },
    },
    { type: 'field', field: insuranceFields.holderFirstName, xs: 12, md: 6 },
    { type: 'field', field: insuranceFields.holderLastName, xs: 12, md: 6 },
    { type: 'field', field: insuranceFields.holderBirthday, xs: 12 },
    { type: 'field', field: insuranceFields.livesOnDifferentAddress, xs: 12 },
    { type: 'field', field: insuranceFields.holderStreet, xs: 12 },
    { type: 'field', field: insuranceFields.holderCity, xs: 12 },
    { type: 'field', field: insuranceFields.holderZipcode, xs: 12 },
    { type: 'field', field: insuranceFields.hasDentalInsurance, xs: 12 },
    {
      type: 'custom',
      key: 'dentalInsuranceCompany',
      xs: 12,
      render: ({ values }) => {
        const { helperText, ...field } = insuranceFields.dentalInsuranceCompany

        if (field.visibleWhen && !field.visibleWhen(values)) return null

        return <InsuranceField helperText={helperText} companiesOptions={insuranceCompanies} {...field} />
      },
    },
    { type: 'field', field: insuranceFields.hasCivilServiceAllowance, xs: 12 },
  ]

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const layout = useMemo(() => insuranceLayout({ insuranceCompanies }), [insuranceCompanies])
  const { t } = useTranslation()

  const submitButton = (
    <Field>
      {(props: FieldProps) => {
        return (
          <SubmitButton loading={loading} onClick={() => props.form.submitForm()} disabled={!props.form.isValid}>
            {t('next')}
          </SubmitButton>
        )
      }}
    </Field>
  )

  const initialErrors = useMemo(
    () => getFormInitialErrors(insuranceFieldsValidationSchema(), initialValues),
    [initialValues]
  )

  return (
    <Formik
      enableReinitialize
      innerRef={innerRef}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={insuranceFieldsValidationSchema()}
      initialErrors={initialErrors}
    >
      <Form>
        <FormLayout portalAttr={portalAttr} items={layout} submitButton={submitButton} />
      </Form>
    </Formik>
  )
}
